import { Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { KartListItem } from '@kangaroo/shared';
import { colors } from '@kangaroo/ui';

import { findSetupAtTime, formatKartSubtitle, setupMatches } from '../../lib/telemetrySessions';

function formatDateTime(value: string) {
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function setupSnapshotRows(setup: KartListItem['setup']) {
  return [
    ['Front track', setup.frontTrackWidthMm != null ? `${setup.frontTrackWidthMm} mm` : '—'],
    ['Rear track', setup.rearTrackWidthMm != null ? `${setup.rearTrackWidthMm} mm` : '—'],
    ['Seat', setup.seatPosition ?? '—'],
    [
      'Gearing',
      setup.gearRatioDriver != null && setup.gearRatioAxle != null
        ? `${setup.gearRatioDriver} / ${setup.gearRatioAxle}`
        : '—',
    ],
    ['Front PSI', setup.frontTirePressurePsi != null ? `${setup.frontTirePressurePsi} psi` : '—'],
    ['Rear PSI', setup.rearTirePressurePsi != null ? `${setup.rearTirePressurePsi} psi` : '—'],
    ['Weight', setup.totalWeightKg != null ? `${setup.totalWeightKg} kg` : '—'],
  ] as const;
}

function formatSetupOptionLabel(entry: KartListItem['setupHistory'][number], index: number) {
  const created = formatDateTime(entry.createdAt);
  return entry.id === 'current' ? `Current setup · ${created}` : `Snapshot ${index + 1} · ${created}`;
}

type SessionKartSetupPanelProps = {
  kart: KartListItem | null;
  recordedAtMs: number | null;
  setupHistoryEntryId?: string | null;
  onSetupHistoryEntryChange?: (entryId: string | null) => void;
  saving?: boolean;
  onOpenVehicle?: () => void;
  variant?: 'default' | 'compact';
};

export function SessionKartSetupPanel({
  kart,
  recordedAtMs,
  setupHistoryEntryId,
  onSetupHistoryEntryChange,
  saving = false,
  onOpenVehicle,
  variant = 'default',
}: SessionKartSetupPanelProps) {
  if (!kart) {
    if (variant === 'compact') {
      return (
        <Typography sx={{ fontSize: '0.72rem', color: '#64748B', lineHeight: 1.45 }}>
          Assign a vehicle to view setup.
        </Typography>
      );
    }

    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 0.5 }}>Session vehicle setup</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          Assign a vehicle to see the setup used for this session.
        </Typography>
      </Box>
    );
  }

  const history = kart.setupHistory.length > 0
    ? kart.setupHistory
    : [{ id: 'current', setup: kart.setup, notes: kart.notes, createdAt: kart.updatedAt }];
  const selectedHistoryEntry = setupHistoryEntryId
    ? history.find((entry) => entry.id === setupHistoryEntryId) ?? null
    : null;
  const sessionSetup = selectedHistoryEntry ?? findSetupAtTime(kart, recordedAtMs);
  const currentDiffers = !setupMatches(sessionSetup.setup, kart.setup);
  const rows = setupSnapshotRows(sessionSetup.setup);

  if (variant === 'compact') {
    return (
      <Box sx={{ pt: 0.25 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 0.45 }}>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 650, color: '#94A3B8' }}>Setup</Typography>
          {currentDiffers ? (
            <Typography sx={{ fontSize: '0.68rem', color: colors.warning.main, fontWeight: 650 }}>
              Changed since session
            </Typography>
          ) : null}
        </Stack>
        <Stack spacing={0.25}>
          {rows.map(([label, value]) => (
            <Stack key={label} direction="row" justifyContent="space-between" spacing={1.5} sx={{ py: 0.1 }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', flexShrink: 0 }}>{label}</Typography>
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  color: value === '—' ? '#475569' : '#CBD5E1',
                  fontWeight: value === '—' ? 500 : 650,
                  textAlign: 'right',
                }}
              >
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {sessionSetup.notes ? (
          <Typography sx={{ fontSize: '0.68rem', color: '#64748B', mt: 0.5, lineHeight: 1.45 }}>
            {sessionSetup.notes}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.75}>
        <Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>Session vehicle setup</Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            #{kart.kartNumber ?? '—'} · {formatKartSubtitle(kart)}
          </Typography>
        </Box>
        {currentDiffers ? (
          <Chip
            size="small"
            label="Changed since session"
            sx={{ bgcolor: 'rgba(245,158,11,0.14)', color: colors.warning.main, fontWeight: 700 }}
          />
        ) : null}
      </Stack>

      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mb: 1 }}>
        {selectedHistoryEntry
          ? `Manually selected ${formatDateTime(selectedHistoryEntry.createdAt)}`
          : recordedAtMs
            ? `Setup active at ${formatDateTime(new Date(recordedAtMs).toISOString())}`
            : 'Using the latest recorded setup snapshot'}
      </Typography>

      {onSetupHistoryEntryChange && history.length > 1 ? (
        <TextField
          select
          size="small"
          label="Setup snapshot"
          value={setupHistoryEntryId ?? ''}
          disabled={saving}
          onChange={(event) => onSetupHistoryEntryChange(event.target.value || null)}
          fullWidth
          sx={{ mb: 1 }}
        >
          <MenuItem value="">Auto from session time</MenuItem>
          {history.map((entry, index) => (
            <MenuItem key={entry.id} value={entry.id}>
              {formatSetupOptionLabel(entry, index)}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75, mb: 1 }}>
        {setupSnapshotRows(sessionSetup.setup).map(([label, value]) => (
          <Box key={label} sx={{ borderRadius: 0.75, bgcolor: 'subtleFill.light', px: 0.9, py: 0.7 }}>
            <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800 }}>{value === '—' ? 'Not set' : value}</Typography>
          </Box>
        ))}
      </Box>

      {sessionSetup.notes ? (
        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mb: 1 }}>{sessionSetup.notes}</Typography>
      ) : null}

      {onOpenVehicle ? (
        <Button size="small" variant="outlined" onClick={onOpenVehicle} sx={{ textTransform: 'none' }}>
          Open vehicle sheet
        </Button>
      ) : null}
    </Box>
  );
}
